<template>
  <section class="about-page">
    <header class="hero">
      <div class="container hero-inner">
        <h1 class="hero-title" data-aos="fade-up">{{ hero.title }}</h1>
        <p class="hero-sub" data-aos="fade-up" data-aos-delay="100">
          {{ hero.subtitle }}
        </p>
      </div>
    </header>

    <AboutIntro />

    <section class="factory-section" data-aos="fade-up">
      <div class="container factory-grid">
        <div class="factory-text">
          <div class="sec-label">MANUFACTURING ORIGIN</div>
          <h2 class="section-title">{{ factory.title }}</h2>
          <div class="title-line"></div>
          <p class="lead">{{ factory.lead }}</p>
          <ul class="strength-list">
            <li v-for="(item, i) in factory.strengths" :key="i">
              <span class="icon">●</span> {{ item }}
            </li>
          </ul>
        </div>
        <div class="factory-gallery">
          <Gallery :images="factoryGallery" />
        </div>
      </div>
    </section>

    <section class="timeline-section">
      <div class="container">
        <h2 class="section-title text-center">20 Years of Heritage</h2>
        <div class="timeline" data-aos="fade-up">
          <div class="timeline-line"></div>
          <div
            v-for="(step, idx) in history"
            :key="idx"
            class="timeline-item"
            :class="{
              left: !isMobile && idx % 2 === 0,
              right: !isMobile && idx % 2 === 1,
            }"
          >
            <div class="marker-wrapper">
              <div class="marker-node"></div>
              <div class="marker-year">{{ step.year }}</div>
            </div>
            <div class="item-card">
              <h3 class="item-title">{{ step.title }}</h3>
              <p class="item-text">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="mission-section" data-aos="fade-up">
      <div class="container">
        <div class="sec-label">THE YOLEE® ADVANTAGE</div>
        <h2 class="section-title">{{ yoleeRole.title }}</h2>
        <div class="title-line"></div>
        <div class="mission-intro">
          <p v-html="yoleeRole.intro1"></p>
          <p v-html="yoleeRole.intro2"></p>
        </div>
        <div class="mission-grid">
          <div
            class="mission-card"
            v-for="(card, i) in yoleeRole.cards"
            :key="i"
          >
            <div class="card-count">{{ i + 1 }}</div>
            <h3>{{ card.title }}</h3>
            <p>{{ card.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="container cta-inner">
        <div class="cta-copy">
          <h2 class="cta-title">Upgrade Your Production Strategy</h2>
          <p class="cta-sub">
            Consult with YOLEE® experts to find the right ABR® machinery
            platform for your facility.
          </p>
        </div>
        <div class="cta-actions">
          <router-link class="btn primary" to="/contact"
            >Consult an Engineer</router-link
          >
          <router-link class="btn outline" :to="{ path: '/categories/main/1' }"
            >View Machinery</router-link
          >
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import Gallery from "@/components/Gallery";
import AboutIntro from "./components/AboutIntro.vue";
import AOS from "aos";
import { aboutAssets, aboutContent } from "@/config/about";

export default {
  name: "AboutPage",
  components: { Gallery, AboutIntro },
  data() {
    return { windowWidth: window.innerWidth };
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 768;
    },
    hero() {
      return aboutContent.hero;
    },
    factory() {
      return aboutContent.factory;
    },
    history() {
      return aboutContent.history;
    },
    yoleeRole() {
      return aboutContent.yoleeRole;
    },
    factoryGallery() {
      return aboutAssets.factoryGallery;
    },
  },
  mounted() {
    document.title = "ABR & YOLEE | 20 Years of Abrasive Machinery Heritage";
    if (AOS) AOS.refresh();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="less" scoped>
@import "~@/assets/styles/variables.less";

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}
.text-center {
  text-align: center;
}
.title-line {
  width: 50px;
  height: 3px;
  background: @color-primary;
  margin: 15px 0 25px;
}
.sec-label {
  font-size: 11px;
  font-weight: 800;
  color: @color-primary;
  letter-spacing: 2.5px;
  margin-bottom: 12px;
  text-transform: uppercase;
}

/* HERO */
.hero {
  padding: 100px 0 60px;
  background: #fdfdfd;
  text-align: center;
  .hero-title {
    font-size: 46px;
    font-weight: 800;
    color: @color-text-title;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
  }
  .hero-sub {
    font-size: 19px;
    color: @color-text-sub;
    max-width: 850px;
    margin: 0 auto;
    line-height: 1.6;
  }
}

/* TIMELINE */
.timeline-section {
  padding: 90px 0;
  background: #f8faf9;
}
.timeline {
  position: relative;
  margin-top: 50px;
}
.timeline-line {
  position: absolute;
  left: 50%;
  width: 1px;
  top: 0;
  bottom: 0;
  background: #dee5e1;
  transform: translateX(-50%);
  @media (max-width: 768px) {
    left: 24px;
  }
}
.timeline-item {
  position: relative;
  width: 50%;
  padding: 30px 50px;
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 60px;
    padding-right: 0;
  }
}
.timeline-item.left {
  left: 0;
  text-align: right;
}
.timeline-item.right {
  left: 50%;
}

.marker-wrapper {
  position: absolute;
  top: 35px;
  width: 60px;
  text-align: center;
}
.timeline-item.left .marker-wrapper {
  right: -30px;
}
.timeline-item.right .marker-wrapper {
  left: -30px;
}
.timeline-item.right {
  @media (max-width: 768px) {
    .marker-wrapper {
      left: -6px;
    }
  }
}
.marker-node {
  width: 10px;
  height: 10px;
  background: @color-primary;
  border-radius: 50%;
  margin: 0 auto 5px;
}
.marker-year {
  font-size: 13px;
  font-weight: 800;
  color: @color-primary;
}

.item-card {
  background: #fff;
  padding: 30px;
  border: 1px solid #e2e8e4;
  border-radius: 2px;
  transition: 0.3s;
  &:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.04);
    border-color: @color-primary;
  }
}
.item-title {
  font-size: 19px;
  margin-bottom: 12px;
  color: @color-text-title;
  font-weight: 700;
}
.item-text {
  font-size: 14.5px;
  color: @color-text-sub;
  line-height: 1.7;
  margin: 0;
}

/* MISSION */
.mission-section {
  padding: 100px 0;
}
.mission-intro p {
  font-size: 16px;
  color: @color-text-sub;
  line-height: 1.8;
  margin-bottom: 15px;
}
.mission-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-top: 45px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}
.mission-card {
  background: #fcfdfc;
  padding: 40px;
  border: 1px solid #f0f4f2;
  position: relative;
  .card-count {
    font-size: 40px;
    font-weight: 800;
    color: rgba(0, 0, 0, 0.02);
    position: absolute;
    top: 20px;
    right: 20px;
  }
  h3 {
    font-size: 19px;
    margin-bottom: 15px;
    color: @color-text-title;
    font-weight: 700;
    position: relative;
    &::after {
      content: "";
      display: block;
      width: 30px;
      height: 2px;
      background: @color-primary;
      margin-top: 8px;
    }
  }
  p {
    font-size: 14.5px;
    color: @color-text-sub;
    line-height: 1.6;
    margin: 0;
  }
}

/* CTA */
.cta-section {
  padding: 90px 0;
  background: @color-primary;
  color: #fff;
}
.cta-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 35px;
  }
}
.cta-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 10px;
  letter-spacing: -0.5px;
}
.cta-sub {
  opacity: 0.85;
  font-size: 17px;
}
.btn {
  display: inline-block;
  padding: 16px 36px;
  font-weight: 700;
  text-decoration: none;
  border-radius: 2px;
  transition: 0.3s;
  text-transform: uppercase;
  font-size: 13px;
  letter-spacing: 1px;
  &.primary {
    background: #fff;
    color: @color-primary;
  }
  &.outline {
    border: 1px solid rgba(255, 255, 255, 0.5);
    color: #fff;
    margin-left: 20px;
    @media (max-width: 768px) {
      margin: 10px 0 0;
    }
    &:hover {
      border-color: #fff;
      background: rgba(255, 255, 255, 0.05);
    }
  }
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
}

/* STRENGTH LIST */
.strength-list {
  list-style: none;
  margin-top: 1rem;
  padding: 0;
  li {
    margin-bottom: 15px;
    font-size: 15px;
    color: @color-text-sub;
    display: flex;
    align-items: flex-start;
    .icon {
      color: @color-primary;
      font-size: 10px;
      margin-right: 12px;
      margin-top: 4px;
    }
  }
}
</style>
